//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { ColumnModel, CorrespondingActionTypeRequestModel } from '../../../api/api.models'

import { TypeHelper, LocalizationHelper } from '../../../../helpers'

import { Enums, Activity } from '../../../../models/common'
import { OpDepositoryElement, OpArticle } from '../../../../models/core'

import { GridOptions, GridController, GridTypes } from '../../../common/grid'

import { ValidationControlsService, SelectBoxControlsService } from '../../../../services/controls'
import { OrderService, OrderAddService } from '../../../../services/order'
import { UserSessionService } from '../../../../services/user-session'

import { IAddOrder, AddOrderOptions, GoAddOrderContentItem } from './add-order.interfaces'

export class OrderAddController implements ng.IComponentController, IAddOrder {

    //#region Members

    public articleGridOptions: GridOptions
    public formOptions: DevExpress.ui.dxFormOptions
    public isLoading: boolean
    public options: AddOrderOptions
    public selectBoxOptions: DevExpress.ui.dxSelectBoxOptions

    private addButton: DevExpress.ui.dxButton
    private articles: OpArticle[]
    private contentGrid: GridController<GoAddOrderContentItem>
    private contentGridItems: GoAddOrderContentItem[]
    private form: DevExpress.ui.dxForm
    private isReadonly: boolean

    //#endregion 


    //#region Ctor

    constructor(
        private orderService: OrderService,
        private orderAddService: OrderAddService,
        private selectBoxControlsService: SelectBoxControlsService,
        private userSessionService: UserSessionService,
        private validationControlsService: ValidationControlsService
    ) {
    }

    //#endregion


    //#region ng.IComponentController

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    //#endregion


    //#region IOrdersAddOrder Implementation

    public getArticles(): OpDepositoryElement[] {
        const objectsToSave: OpDepositoryElement[] = []

        _.forEach(this.contentGridItems, (contentItem: GoAddOrderContentItem) => {
            objectsToSave.push(...contentItem.toContentList())
        })

        return objectsToSave
    }

    public refresh(): void {
        if (this.contentGrid != null) {
            this.contentGrid.dxGrid.repaint()
        }
    }

    //#endregion


    //#region Component

    private initialize(): void {
        this.isLoading = true
        this.setOptions()

        this.orderService
            .getArticles()
            .then((articles: OpArticle[]) => {
                this.articles = articles
                this.isLoading = false

                this.contentGridItems = []

                this.initializeForm(articles)
                this.initializeContent(this.options.content, articles)
                this.onInitialized()
            })
    }

    private changeSelection(idArticle: number): void {
        const existingContentItem: GoAddOrderContentItem = this.getContentItem(idArticle)

        if (existingContentItem != null) {
            this.resetForm(existingContentItem)
            this.updateContentGridSelection(existingContentItem.idArticle)
            this.toggleAddButtonText(true)
        } else {
            this.resetForm()
            this.updateContentGridSelection()
            this.toggleAddButtonText()
        }
    }

    private removeContentItem(idArticle: number): void {
        const existingContentItem: GoAddOrderContentItem = this.getContentItem(idArticle)

        if (existingContentItem != null) {
            switch (existingContentItem.opState) {
                case Enums.OpChangeState.New:
                    this.contentGridItems = _.filter(this.contentGridItems, (contentGridItem: GoAddOrderContentItem) => {
                        return contentGridItem.idArticle !== idArticle
                    })

                    break

                case Enums.OpChangeState.Delete:
                    existingContentItem.opState = existingContentItem.previousOpSate
                    this.contentGrid.setItemDisplayState(existingContentItem, existingContentItem.opState)

                    break

                default:
                    existingContentItem.opState = Enums.OpChangeState.Delete
                    this.contentGrid.setItemDisplayState(existingContentItem, existingContentItem.opState)

                    break
            }
        }

        this.refreshGrid()
    }

    private updateContentItems(formContentItem: GoAddOrderContentItem): void {
        const existingContentItem: GoAddOrderContentItem = this.getContentItem(formContentItem.idArticle)

        if (existingContentItem != null) {
            existingContentItem.copyFormData(formContentItem, this.articles)

            switch (existingContentItem.opState) {
                case Enums.OpChangeState.Loaded:
                case Enums.OpChangeState.Delete:
                    existingContentItem.opState = Enums.OpChangeState.Modified
                    break
            }

            this.contentGrid.setItemDisplayState(existingContentItem, existingContentItem.opState)

        } else {
            this.contentGridItems.push(formContentItem)
            this.contentGrid.setItemDisplayState(formContentItem, formContentItem.opState)
        }

        this.refreshGrid()
    }

    //#endregion


    //#region Events

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    //#endregion


    //#region Content

    private getContentItem(idArticle): GoAddOrderContentItem {
        return _.find(this.contentGridItems, (contentGridItem: GoAddOrderContentItem) => {
            return contentGridItem.idArticle === idArticle
        })
    }

    //#endregion


    //#region Grid

    private getContentGridColumns(): ColumnModel[] {
        const columnFunc = (gridData: any) => {
            const contentItem: GoAddOrderContentItem = this.contentGrid.getSourceObject(gridData)
            const buttonOptions: DevExpress.ui.dxButtonOptions = {
                hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Delete),
                icon: 'remove',

                onClick: (e: any): any => {
                    this.clearForm()
                    this.removeContentItem(contentItem.idArticle)
                }
            }

            if (contentItem.opState === Enums.OpChangeState.Delete) {
                buttonOptions.hint = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Add)
                buttonOptions.icon = 'add'
            }

            return buttonOptions
        }

        return this.orderAddService.getOrderContentColumns(this.isReadonly ? null : columnFunc)
    }

    private initializeContent(contentList: OpDepositoryElement[], articles: OpArticle[]): void {
        this.contentGridItems = GoAddOrderContentItem.fromContentList(contentList, articles)

        this.initializeContentGrid()
    }

    private initializeContentGrid(): void {
        this.articleGridOptions = {
            addCustomFilters: true,
            columnsDictionary: this.getContentGridColumns(),
            name: null,
            type: GridTypes.PAGINATED_SMALL,

            buttonsOptions: {
                enabled: true,
                isRefreshButonHidden: true,
            },

            allowColumnReordering: !this.userSessionService
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.OrderContent),

            getDataSource: () => {
                return this.contentGridItems
            },

            getDataSourceKey: (contentItem: GoAddOrderContentItem) => {
                return contentItem.idArticle
            },

            onInitialized: (e, grid: GridController<GoAddOrderContentItem>) => {
                this.contentGrid = grid
            },

            onSelectionChanged: (e: any) => {
                this.onContentGridRowClick(e)
            }
        }
    }

    private onContentGridRowClick(e: any): void {
        const selectedGridItem: GoAddOrderContentItem = this.contentGrid.selectedItem

        if (selectedGridItem != null) {
            this.changeSelection(selectedGridItem.idArticle)
        }
    }

    private refreshGrid(): void {
        this.contentGrid.dxGrid.refresh()
    }

    private updateContentGridSelection(idArticle: number = null): void {
        if (idArticle != null) {
            this.contentGrid.selectById([idArticle])
        } else {
            this.contentGrid.dxGrid.clearSelection()
        }
    }

    //#endregion


    //#region Form

    private clearForm(): void {
        this.form.resetValues()
    }

    private resetForm(contentItem: GoAddOrderContentItem = null): void {
        if (contentItem != null) {
            this.form.option('formData', contentItem.toFormData())
        } else {
            this.form.option('formData.notes', null)
            this.form.option('formData.amount', 1)
        }
    }

    private getFormData(): void {
        return this.form.option('formData')
    }

    private initializeForm(articles: OpArticle[]): void {
        const articleSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions = this.selectBoxControlsService.getSelectBoxOptions({
            items: articles
        })

        articleSelectBoxOptions.disabled = this.isReadonly
        articleSelectBoxOptions.searchEnabled = true
        articleSelectBoxOptions.onValueChanged = (e: any) => {
            this.onArticleSelectboxValueChanged(e)
        }

        const leftGroup: any = {
            itemType: "group",
            items: [{
                dataField: "idArticle",
                editorType: "dxSelectBox",
                editorOptions: articleSelectBoxOptions,

                validationRules: [
                    this.validationControlsService.getRequiredRule()
                ]
            }, {
                dataField: 'amount',
                editorType: "dxNumberBox",

                editorOptions: {
                    disabled: this.isReadonly,
                    min: 1,
                    showClearButton: true,
                    showSpinButtons: true,
                    value: 1,
                },

                validationRules: [
                    this.validationControlsService.getRequiredRule()
                ]
            }]
        }

        if (!this.isReadonly) {
            leftGroup.items.push({
                editorType: "dxButton",
                cssClass: 'orders-add-order__add_button',

                editorOptions: {
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Add),

                    onClick: (e: any) => {
                        this.onSubmitFormButtonClick()
                    },

                    onInitialized: (e: any) => {
                        this.addButton = e.component
                    }
                }
            })
        }

        const rightGroup: any = {
            cssClass: 'orders-add-order__notes-form-group',
            itemType: "group",
            items: [{
                cssClass: 'orders-add-order__notes-textarea',
                dataField: "notes",
                editorType: "dxTextArea",

                editorOptions: {
                    disabled: this.isReadonly,
                }
            }]
        }

        this.formOptions = {
            colCount: 2,
            items: [leftGroup, rightGroup],

            onInitialized: (e: any) => {
                this.form = e.component
            }
        }



        // this.formOptions.items[0].
    }

    private onArticleSelectboxValueChanged(e: any): void {
        this.changeSelection(e.value)
    }

    private onSubmitFormButtonClick(): void {
        const isFormValid: DevExpress.ui.dxValidationGroupResult = this.form.validate()

        if (isFormValid == null || isFormValid.isValid) {
            const contentItem: GoAddOrderContentItem = GoAddOrderContentItem.fromFormData(this.getFormData(), this.articles)

            this.updateContentItems(contentItem)
        }
    }

    private toggleAddButtonText(isUpdating: boolean = false): void {
        if (this.addButton != null) {
            const buttonText = isUpdating ? LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Edit) : LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Add)

            this.addButton.option('text', buttonText)
        }
    }


    //#endregion


    //#region Options

    public setDefaults(): void {
        this.isReadonly = false
    }

    public setOptions(): void {
        this.setDefaults()

        if (this.options != null) {
            if (this.options.readonly != null) {
                this.isReadonly = this.options.readonly
            }
        }
    }

    //#endregion
}